import { useEffect, useState } from 'react'
import { assignRatioGroup, type RatioGroup, type RatioThresholds } from '@bainner/banner-core'

import { apiClient, ApiError } from '../../lib/api-client.js'
import { Button } from '../ui/Button.js'
import { CodeField } from '../ui/CodeField.js'
import { Scrim } from '../ui/Scrim.js'
import { Tabs } from '../ui/Tabs.js'
import { IconClose16, IconInfo, IconTrash16 } from '../icons/index.js'

/**
 * FigmaPluginLightbox — plugin-dialog item, Epic 49 seam-wiring pass.
 *
 * Figma `JXxc2k1zy324lED5hVsANM`, node `20:2502` ("New Campaign - Plugin Awaiting") — the
 * AWAITING state — plus its SECOND state, `20:2675` / `20:4804` (both "New Campaign - Plugin
 * Master Added"), added in this pass. See "THE SECOND STATE" below for that pair.
 *
 * ⚠ PROVENANCE, stated plainly rather than left for a reader to misjudge as the dead-page rule
 * being ignored. Node `20:2502` sits on page `20:2501 "Wireframe"`, which `DESIGN-HANDOVER.md`
 * (line 6, line 674-677) records as SUPERSEDED — that page no longer exists in the live Figma
 * file; the current design lives on page `113:2821 "Design"`. This node is used anyway because
 * (a) the owner pointed at it directly by node id, and (b) `113:2821`'s own evidence draws no
 * replacement for this exact dialog — grepping `DESIGN-HANDOVER.md` for a home for the plugin
 * import/tutorial modal turns up only the OLD `20:2502`/`20:2675` comparison used for copy
 * precedent (§9-adjacent table, "Right-panel action-row copy"), never a `113:*` frame of the
 * dialog itself. So this is the one surface in this codebase's design coverage that is built
 * from an otherwise-retired page — a deliberate exception, not an oversight, and superseded by
 * a real `113:*` frame the moment one exists. `20:2675`/`20:4804` sit on the SAME `20:2501` page
 * as `20:2502`, so this exception covers all three nodes together, not `20:2502` alone.
 *
 * WHAT THE AWAITING NODE DRAWS, reproduced structurally: a 2-tab strip (`Eklenti` / `Dosya
 * Yükle`), a placeholder graphic, a heading, a `CodeField` holding the campaign id with a copy
 * affordance, two lines of body copy, a divider, and a footer with the existing plugin-download
 * button. All Turkish strings below are the node's own — this is not a translation exercise.
 *
 * THREE HONESTY CALLS FOR THE AWAITING STATE, each recorded here because two of the node's
 * elements have nothing behind them and the third can't be read off the node at all:
 *
 * 1. `Dosya Yükle` (File Import) ships DISABLED. D-IMPORT-2 is open — there is no file/REST
 *    import engine anywhere in this codebase (see `.claude/UX-BRIEF.md` §"Design import",
 *    `DECISIONS-PENDING-2026-08-25.md`). This mirrors `CreateCampaignModal.tsx`'s own
 *    `Tabs` call for the identical reason (`{ id: 'file', label: 'File Import', disabled: true }`)
 *    verbatim — matching that accepted precedent rather than inventing a second disabled
 *    treatment, per instruction.
 *
 * 2. The node's 3-dot carousel indicator (`Frame 99`, three ellipses under the body copy) is
 *    OMITTED. A dot row implies more than one swipeable panel; this dialog has exactly one
 *    (the `Eklenti` tab's content — `Dosya Yükle` has none, disabled per #1 above). Drawing 3
 *    static dots with nothing behind them to page through would be a fake carousel — the same
 *    category of dishonesty rule 2 forbids for a wired-to-nothing control. Chose to omit rather
 *    than render a 1-dot "honest" version, since a single dot conveys the same false "there is
 *    more than one screen" idea a 3-dot row does.
 *
 * 3. The campaign-id `CodeField`. Both mount points (`AppShell.tsx`, `WorkspaceAppShell.tsx`)
 *    render this from a persistent, page-level "Figma Plugin" trigger with no campaign in
 *    route — neither passes `campaignId` (or the new `masters`/`campaignName` below) to this
 *    component today. Optional `campaignId` prop, defaulting to `null`; while null the field is
 *    replaced by an honest "not available outside a campaign" line (English, unevidenced state —
 *    same convention `CreateCampaignModal.tsx`'s own "Campaign ID — not yet available" placeholder
 *    uses, so an un-designed engineering string is never mistaken for invented Turkish product
 *    copy) instead of an empty or fabricated id.
 *
 * KEPT, RECORDED: the node draws no dialog close (✕) — this exact modal shape (tabs directly
 * inside the card, no header row) has nothing resembling AppShell/CreateCampaignModal's header
 * bar for one to live in. The working close behaviour (✕ button, overlay click, Escape) already
 * shipped and has real value (the modal has no other way out); dropping it because the node has
 * no drawn home for it would be deleting working behaviour rule 5 forbids. Kept as a small
 * floating control in the card's top-right corner, styled like `CreateCampaignModal.tsx`'s own
 * close button — and kept ACROSS both states, not just the awaiting one (same rule).
 *
 * The download button and its S3-presigned-URL flow are UNCHANGED from the previous
 * implementation (Epic 31 Phase 10) — only the surrounding chrome was redesigned.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * THE SECOND STATE — `20:2675` / `20:4804`, both named "New Campaign - Plugin Master Added".
 *
 * RELATIONSHIP BETWEEN THE PAIR, established by reading both (`get_metadata` + `get_design_
 * context` on each): this is ONE state, not two — the same card, same tab strip, same "Kampanya
 * ID" row, same "Master Boyutlarınız" section, same primary button — captured at two different
 * DATA snapshots, not two different moments in a flow:
 *   - `20:4804` — exactly ONE master has arrived (`300x250`, "Kare Master" only) → the warning
 *     banner below the row IS drawn: "En iyi sonuçlar için yatay, kare ve dikey oranda 3 master
 *     ekleyin." (verbatim match to the awaiting state's own persistent tip, not a reworded copy).
 *   - `20:2675` — THREE masters have arrived, one per canonical ratio group (`300x250` Kare,
 *     `728x90` Yatay, `120x600` Dikey) → the warning banner is ABSENT.
 * The card's height differs (627 vs 584) but that delta is fully explained by content (two more
 * 40px rows vs. one 41px warning box) — nothing else about the shell changes. So the warning is
 * DATA-DRIVEN, not a separate screen: shown iff the campaign's masters don't yet cover all three
 * canonical ratio groups. Classified with `assignRatioGroup` from `@bainner/banner-core` — the
 * SAME classifier `SourceMasterPicker.tsx` / `ImportLeftPanel.tsx` / `RatioThresholdPanel.tsx`
 * already use — not a fourth hand-rolled threshold copy.
 *
 * WHAT CHANGES vs. the awaiting state (read from both nodes' own `get_design_context`, not
 * assumed to be "just a swapped illustration"): the placeholder graphic, the "Başlamak için
 * eklentiden aktarın" heading, and both body paragraphs are GONE. In their place: a "Kampanya
 * Adı" block, a "Kampanya ID" row (restyled — a grey pill + compact icon-copy button, not the
 * awaiting state's wide filled-button `CodeField`), a divider, and "Master Boyutlarınız" — one
 * row per arrived master (size, ratio-group role, copyable id, delete) plus the conditional
 * warning. The plugin-download FOOTER (divider + "Henüz eklentiyi yüklemediniz mi?" + button) is
 * ALSO gone in this state — once a master exists you are no longer told to go install the thing
 * that just worked. The primary button, "Oluşturmaya Başla", sits directly under the list with
 * no divider above it (confirmed from `get_metadata`: the button instance is a direct sibling of
 * the list frame, not preceded by a divider node).
 *
 * FOUR MORE HONESTY CALLS, specific to this second state:
 *
 * 4. IS THERE ANYTHING TO DRIVE THE TRANSITION? No — and this is not faked. Both mount points
 *    pass neither `campaignId` NOR the new `masters`/`campaignName` props below, so this branch
 *    is UNREACHABLE in the shipped app today — byte-identical behaviour to before this change.
 *    Checked for a live "a master arrived" signal and found none: `PluginHandoffPanel.tsx` (the
 *    one existing component that self-fetches a campaign's masters) fetches once on mount plus
 *    once after ITS OWN delete action — no interval, no websocket, no query invalidation fires
 *    when the Figma plugin uploads a design from outside the browser session. So this file does
 *    not poll a signal that doesn't exist, and does not fabricate one either. `masters` and
 *    `campaignName` are left CALLER-SUPPLIED instead — the same contract `ImportLeftPanel.tsx`
 *    and `CreateCampaignModal.tsx` already settled for master lists (both of their own file
 *    headers say, verbatim, "this component does not call getVariants/getBanners itself").
 *    Precisely what's missing, so it isn't lost:
 *      a. Neither `AppShell.tsx` nor `WorkspaceAppShell.tsx` (not owned by this task) threads a
 *         campaign into this dialog yet — that's the wiring gap, not an engine gap.
 *      b. `WorkspaceAppShell.tsx` already computes a real campaign id in scope, for an unrelated
 *         purpose (`useMatch('/campaigns/:id')`) — CORRECTING an inaccuracy an earlier revision
 *         of this header stated ("useMatch/useParams for a campaign id exists nowhere upstream").
 *         That mechanism exists; it is simply not threaded to this dialog. Wiring it through is a
 *         small follow-up in a file this task does not own.
 *      c. Even once `masters` is threaded through, this dialog will show whatever the caller's
 *         own fetch last returned — it will not update itself the instant the plugin finishes a
 *         new import mid-session. That refresh policy (poll, manual refresh, a future WS event)
 *         is the caller's to decide; nothing here pretends otherwise.
 *
 * 5. "Kampanya Adı" — RESOLVED 2026-08-31, and worth reading as a cautionary note.
 *    The node draws an editable bordered field, captioned "Kampanya ismini AI önerir, dilerseniz
 *    değiştebilirsiniz". This shipped as read-only text on the stated grounds that "no
 *    campaign-rename endpoint exists anywhere in `api-client.ts`" — and every word of that was
 *    true while the conclusion drawn from it was wrong. `PUT /campaigns/:campaignId` has existed
 *    and worked the whole time; what was missing was a three-line WRAPPER in `api-client.ts`.
 *    Because that file is the SPA's only sanctioned way to reach the gateway, its absence read as
 *    the server's absence to four separate authors, who each wrote the belief down. The wrapper
 *    now exists, so the field is what the node draws — see `CampaignNameField` for the save,
 *    revert and failure behaviour. The block still renders nothing when the caller has no name to
 *    give (same "omit when absent" convention `ImportLeftPanel.tsx`'s `breadcrumb` prop uses).
 *    STILL absent, and still not faked: the AI name SUGGESTION. No such call exists, so the
 *    caption stays off rather than promising something that never arrives.
 *
 * 6. Per-master delete. Wired for real: `apiClient.deleteVariant(campaignId, variantId)`, the
 *    EXACT call `PluginHandoffPanel.tsx` / `CreateCampaignModal.tsx` / `ImportLeftPanel.tsx` all
 *    already make for the identical action. Two-step confirm, visible (not `title`-only) failure
 *    text — this is a fourth instance of that same small state machine, restyled to this node's
 *    `32×32` white circular button, not a fourth DIVERGING implementation of it.
 *
 * 7. "Oluşturmaya Başla". No route, endpoint, or created spec is evidenced anywhere behind this
 *    button in this node or its neighbours — it is not a generation trigger (there is no
 *    `specId` yet at this point of the flow to generate against) and no navigation target this
 *    codebase's routes document. Wired to the one real, working behaviour this dialog already
 *    has: `onClose`. Recorded as an interpretation of "you're done looking at this tutorial," not
 *    a certainty read off the node.
 */

/** One master design that has arrived from the plugin for the campaign this dialog is scoped to.
 * Mirrors `ImportLeftPanelMaster` / `CreateCampaignMasterRow`'s shape closely enough to be filled
 * from the same source data, but kept local (this file's own contract) rather than importing
 * from either sibling file. */
export interface FigmaPluginLightboxMaster {
  /** The master variant's id — copied via its row's Code Field, and the delete target. */
  variantId: string
  /** Native size of the imported design — drives both the size line and (via `assignRatioGroup`)
   * the "Kare/Yatay/Dikey Master" role line. */
  width: number
  height: number
}

const TAB_ITEMS = [
  { id: 'plugin', label: 'Eklenti' },
  // Matches CreateCampaignModal.tsx's identical `File Import` tab: D-IMPORT-2 is open, so this
  // ships disabled rather than pointing at a route with no engine behind it.
  { id: 'file', label: 'Dosya Yükle', disabled: true },
] as const

const RATIO_LABEL_TR: Record<RatioGroup, string> = {
  horizontal: 'Yatay',
  square: 'Kare',
  vertical: 'Dikey',
}
const ALL_RATIO_GROUPS: readonly RatioGroup[] = ['horizontal', 'square', 'vertical']

/** "300x250" — the node's OWN literal glyph is a plain lowercase "x", not "×". `ImportLeftPanel.
 * tsx`'s sibling surface uses "×" for the same concept; each file follows its own node rather
 * than forcing agreement between two independently-drawn frames. */
function formatDims(dims: { width: number; height: number }): string {
  return `${dims.width}x${dims.height}`
}

/** "Yatay Master" / "Kare Master" / "Dikey Master" — see the file header's note on why this
 * calls the shared `assignRatioGroup` rather than hand-rolling the threshold check again. */
function roleLabel(dims: { width: number; height: number }, thresholds?: RatioThresholds): string {
  return `${RATIO_LABEL_TR[assignRatioGroup(dims.width, dims.height, thresholds)]} Master`
}

/** `null` when the campaign's masters already cover all three canonical ratio groups (node
 * `20:2675`); the node's own warning sentence otherwise (node `20:4804`) — see the file header's
 * "THE SECOND STATE" section for how this was established from the pair. */
function missingRatioGroupWarning(
  masters: readonly FigmaPluginLightboxMaster[],
  thresholds?: RatioThresholds,
): string | null {
  const covered = new Set(masters.map((m) => assignRatioGroup(m.width, m.height, thresholds)))
  const allCovered = ALL_RATIO_GROUPS.every((g) => covered.has(g))
  if (allCovered) return null
  return 'En iyi sonuçlar için yatay, kare ve dikey oranda 3 master ekleyin.'
}

/**
 * Per-master delete — two-step confirm, the SAME `apiClient.deleteVariant` call
 * `PluginHandoffPanel.tsx` / `CreateCampaignModal.tsx` / `ImportLeftPanel.tsx` already make,
 * restyled to this node's `32×32` white circular button (honesty call #6 above).
 */
function DeleteMasterButton({
  campaignId,
  variantId,
  label,
  onDeleted,
}: {
  campaignId: string
  variantId: string
  label: string
  onDeleted: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const remove = () => {
    setBusy(true)
    setFailure(null)
    apiClient
      .deleteVariant(campaignId, variantId)
      .then(() => {
        setBusy(false)
        setConfirming(false)
        onDeleted()
      })
      .catch((err: unknown) => {
        setFailure(err instanceof ApiError ? err.message : 'Silinemedi — tekrar deneyin')
        setBusy(false)
        setConfirming(false)
      })
  }

  if (confirming) {
    return (
      <span className="flex shrink-0 items-center gap-small">
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          className="cursor-pointer whitespace-nowrap rounded-150 bg-error-border px-small-medium py-xsmall text-body-small-medium text-white hover:bg-error-text disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? 'Siliniyor…' : 'Kalıcı sil'}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="cursor-pointer whitespace-nowrap rounded-150 border border-border-primary bg-transparent px-small-medium py-xsmall text-body-small-medium text-text-secondary hover:bg-surface-tertiary disabled:cursor-not-allowed"
        >
          Vazgeç
        </button>
      </span>
    )
  }

  return (
    <span className="flex shrink-0 flex-col items-end gap-xsmall">
      <button
        type="button"
        onClick={() => setConfirming(true)}
        aria-label={`${label} master'ını sil`}
        className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-surface-white text-icon-secondary hover:bg-surface-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-primary focus-visible:ring-offset-1"
      >
        <IconTrash16 />
      </button>
      {failure ? (
        <span className="whitespace-nowrap text-body-small-regular text-error-text">{failure}</span>
      ) : null}
    </span>
  )
}

/** One master row — size + ratio-group role on the left, a compact copy pill + delete on the
 * right. Node `20:2846`/`20:4982` etc.: `CodeField`'s existing opt-in `variant="icon"` is reused
 * rather than forking a third bespoke pill style inside this file (rule: reuse the project's
 * existing components before writing new ones). */
function MasterRow({
  campaignId,
  master,
  ratioThresholds,
  onDeleted,
}: {
  campaignId: string
  master: FigmaPluginLightboxMaster
  ratioThresholds?: RatioThresholds | undefined
  onDeleted: () => void
}) {
  const sizeLabel = formatDims(master)
  const role = roleLabel(master, ratioThresholds)
  const combinedLabel = `${sizeLabel} ${role}`

  return (
    <div
      data-testid={`figma-plugin-master-row-${master.variantId}`}
      className="flex items-center gap-large"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-small-medium text-text-default">{sizeLabel}</p>
        <p className="truncate text-body-small-regular text-text-secondary">{role}</p>
      </div>
      <CodeField
        value={master.variantId}
        variant="icon"
        ariaLabel={`${combinedLabel} kimliğini kopyala`}
        className="w-[200px] shrink-0"
      />
      <DeleteMasterButton
        campaignId={campaignId}
        variantId={master.variantId}
        label={combinedLabel}
        onDeleted={onDeleted}
      />
    </div>
  )
}

/**
 * "Kampanya Adı" — a real, saving field as of 2026-08-31.
 *
 * It shipped as read-only text, and honesty call #5 in this file's header explained why: "a
 * bordered, input-shaped box that silently discards every keystroke on close would be a worse lie."
 * That reasoning was right, and its PREMISE was wrong. `PUT /campaigns/:campaignId` has existed and
 * worked the whole time — agency-guarded, with a real update behind it. What was missing was a
 * three-line wrapper in `api-client.ts`, and because that file is the SPA's only sanctioned way to
 * reach the gateway, its absence read as "the capability does not exist" to four separate authors.
 * The wrapper is now there, so the node's editable-looking field can be what it is drawn as.
 *
 * Saves on blur and on Enter; Escape reverts. An unchanged or empty value never calls the API. A
 * failure says so in words and KEEPS the user's text — the one thing worse than not saving is
 * discarding what they typed while claiming nothing happened.
 *
 * Still NOT built: the caption's AI name suggestion ("Kampanya ismini AI önerir"). No such call
 * exists anywhere, so the caption stays off rather than promising a suggestion that never comes.
 */
function CampaignNameField({
  campaignId,
  name,
  onRenamed,
}: {
  campaignId: string
  name: string
  onRenamed?: ((next: string) => void) | undefined
}) {
  const [draft, setDraft] = useState(name)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  // The caller owns the name; if it changes underneath us (a refetch, another surface), follow it
  // — but never while the user is mid-edit with unsaved text.
  useEffect(() => {
    if (!busy) setDraft(name)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `busy` must not re-sync the draft.
  }, [name])

  async function commit() {
    const next = draft.trim()
    if (next === '' || next === name) {
      setDraft(name)
      setFailure(null)
      return
    }
    setBusy(true)
    setFailure(null)
    try {
      await apiClient.updateCampaign(campaignId, { name: next })
      onRenamed?.(next)
    } catch {
      setFailure('Kampanya adı kaydedilemedi. Yazdığınız metin duruyor, tekrar deneyebilirsiniz.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <label className="text-body-small-medium text-text-default" htmlFor="campaign-name-field">
        Kampanya Adı
      </label>
      <input
        id="campaign-name-field"
        value={draft}
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          } else if (e.key === 'Escape') {
            setDraft(name)
            setFailure(null)
          }
        }}
        className="mt-xsmall w-full rounded-150 border border-border-primary px-small py-xsmall text-body-default-regular text-text-default outline-none focus:border-border-secondary-hover disabled:cursor-not-allowed disabled:text-text-disabled"
      />
      {failure !== null && (
        <p role="alert" className="mt-xsmall text-body-small-regular text-error-text">
          {failure}
        </p>
      )}
    </div>
  )
}

/** The "Master Added" state's body + footer — rendered instead of the awaiting body/footer once
 * a real campaign with at least one arrived master is known. See the file header's "THE SECOND
 * STATE" section. */
function MasterAddedBody({
  campaignId,
  campaignName,
  masters,
  ratioThresholds,
  onMastersChanged,
  onDone,
}: {
  campaignId: string
  campaignName: string | null
  masters: readonly FigmaPluginLightboxMaster[]
  ratioThresholds?: RatioThresholds | undefined
  onMastersChanged?: (() => void) | undefined
  onDone: () => void
}) {
  const warning = missingRatioGroupWarning(masters, ratioThresholds)

  return (
    <>
      <div className="flex flex-col gap-large px-xlarge py-xlarge">
        {campaignName ? (
          // Honesty call #5, REVISED — the rename endpoint was there all along; see
          // `CampaignNameField`'s own doc for what was actually missing and why it read as absent.
          <CampaignNameField
            campaignId={campaignId}
            name={campaignName}
            {...(onMastersChanged ? { onRenamed: () => onMastersChanged() } : {})}
          />
        ) : null}

        <div className="flex items-center gap-large">
          <div className="min-w-0 flex-1">
            <p className="text-body-small-medium text-text-default">Kampanya ID</p>
            <p className="text-body-small-regular text-text-secondary">
              Bu kampanyaya aktarmak için kullanın
            </p>
          </div>
          <CodeField
            value={campaignId}
            variant="icon"
            ariaLabel="Kampanya kimliğini kopyala"
            className="w-[240px] shrink-0"
          />
        </div>

        <div className="border-t border-border-primary" />

        <div className="flex flex-col gap-large">
          <div>
            <p className="text-body-small-medium text-text-default">Master Boyutlarınız</p>
            <p className="text-body-small-regular text-text-secondary">
              Eklentiden gelen boyutlar. İlgili masterı değiştirmek için ona ait ID’yi
              kullanabilirsiniz.
            </p>
          </div>

          <div className="flex flex-col gap-medium">
            {masters.map((m) => (
              <MasterRow
                key={m.variantId}
                campaignId={campaignId}
                master={m}
                ratioThresholds={ratioThresholds}
                onDeleted={() => onMastersChanged?.()}
              />
            ))}
          </div>

          {warning ? (
            <div
              role="status"
              className="flex items-start gap-medium rounded-200 border border-warning-border bg-warning-surface p-large text-body-default-regular text-warning-text"
            >
              {/* No dedicated warning glyph exists in the shipped icon set — `IconInfo` reused,
                  same convention `ImportLeftPanel.tsx`'s own notice box already established. */}
              <IconInfo size={16} className="mt-[1px] shrink-0" />
              <p className="min-w-0 flex-1">{warning}</p>
            </div>
          ) : null}
        </div>
      </div>

      {/* No divider above the button here — `get_metadata` shows the button instance as a direct
          sibling of the list frame, unlike the awaiting footer's divider + download button. */}
      <div className="flex justify-center px-large pb-xlarge">
        <Button variant="primary" size="lg" onClick={onDone}>
          Oluşturmaya Başla
        </Button>
      </div>
    </>
  )
}

export function FigmaPluginLightbox({
  open,
  onClose,
  campaignId = null,
  campaignName = null,
  masters = [],
  ratioThresholds,
  onMastersChanged,
}: {
  open: boolean
  onClose: () => void
  /**
   * The campaign this dialog's import id belongs to. `null` (the default, and what both
   * shipped mount points pass today — see the file header) renders the honest
   * "not available outside a campaign" line instead of the `CodeField`, and keeps this
   * dialog in the awaiting state regardless of `masters`.
   */
  campaignId?: string | null
  /**
   * The campaign's own display name. EDITABLE as of 2026-08-31 (honesty call #5) — the rename
   * endpoint was always real; only its `api-client.ts` wrapper was missing. The caller still owns
   * the value: this component saves through `apiClient.updateCampaign` and reports the new name
   * via `onMastersChanged`, it does not hold the truth. Optional; the "Kampanya Adı" block renders
   * nothing when omitted, matching `ImportLeftPanel.tsx`'s `breadcrumb` convention.
   */
  campaignName?: string | null
  /**
   * Master designs that have arrived from the plugin for `campaignId`, already fetched by the
   * CALLER — this component does not call `getVariants`/`getBanners` itself (see honesty call
   * #4). `[]` (the default) keeps the dialog in its awaiting state; one or more, together with a
   * non-null `campaignId`, switches it to the "Master Added" state described in the file header.
   */
  masters?: readonly FigmaPluginLightboxMaster[]
  /** The campaign's ratio-group boundaries; omitted → `assignRatioGroup`'s defaults. */
  ratioThresholds?: RatioThresholds | undefined
  /** Fired after a master delete succeeds, so the caller can refetch `masters` — same contract
   * `ImportLeftPanel.tsx` / `CreateCampaignModal.tsx` / `PluginHandoffPanel.tsx` already use. */
  onMastersChanged?: (() => void) | undefined
}) {
  const [tab, setTab] = useState<string>('plugin')
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const download = async () => {
    setDownloading(true)
    setError(null)
    try {
      const { url } = await apiClient.getPluginDownloadUrl()
      // Presigned S3 GET — a plain navigation triggers the browser download.
      window.location.assign(url)
    } catch (err) {
      setError(
        err instanceof ApiError && err.statusCode === 404
          ? 'Plugin paketi henüz yayınlanmamış — yöneticinize bildirin.'
          : 'İndirme bağlantısı alınamadı — tekrar deneyin.',
      )
    } finally {
      setDownloading(false)
    }
  }

  // See the file header's "THE SECOND STATE" section: a real campaign AND at least one arrived
  // master, both genuinely present, not inferred or faked.
  const masterAddedCampaignId = campaignId != null && masters.length > 0 ? campaignId : null

  return (
    <>
      <Scrim onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-large"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        {...(masterAddedCampaignId
          ? { 'aria-label': 'İçe aktarılan masterlarınız' }
          : { 'aria-labelledby': 'figma-plugin-lightbox-title' })}
      >
        <div
          className="relative w-[640px] max-w-full rounded-200 border border-border-primary bg-surface-white shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Kapat"
            className="absolute right-medium top-medium cursor-pointer text-icon-secondary hover:text-icon-default"
          >
            <IconClose16 />
          </button>

          <div className="flex justify-center border-b border-border-primary px-large pt-xlarge pb-medium">
            <Tabs
              items={TAB_ITEMS}
              value={tab}
              onChange={setTab}
              size="md"
              label="İçe aktarma yöntemi"
            />
          </div>

          {masterAddedCampaignId ? (
            <MasterAddedBody
              campaignId={masterAddedCampaignId}
              campaignName={campaignName}
              masters={masters}
              ratioThresholds={ratioThresholds}
              onMastersChanged={onMastersChanged}
              onDone={onClose}
            />
          ) : (
            <>
              <div className="flex flex-col items-center gap-medium px-xlarge py-xlarge text-center">
                {/* Centred placeholder graphic — `Rectangle 22` on the node has no glyph inside it
                    (no vector child in `get_metadata`), so this is a plain decorative box, not an
                    icon this codebase's accepted icon set is missing. */}
                <div aria-hidden="true" className="h-8 w-8 rounded-150 bg-surface-tertiary" />

                <h2
                  id="figma-plugin-lightbox-title"
                  className="text-body-default-medium text-text-default"
                >
                  Başlamak için eklentiden aktarın
                </h2>

                {campaignId ? (
                  <CodeField value={campaignId} className="w-full max-w-sm" />
                ) : (
                  // Honest fallback — see the file header, honesty call #3. English on purpose:
                  // this exact "no campaign in context" state has no Figma frame and no Turkish
                  // candidate anywhere, so it borrows CreateCampaignModal.tsx's own convention for
                  // an unevidenced engineering string rather than inventing Turkish copy.
                  <p className="text-body-default-regular text-text-disabled">
                    Campaign ID — not available outside a campaign
                  </p>
                )}

                <p className="text-body-default-regular text-text-secondary">
                  Bainner Figma eklentisini kurun, giriş yapın ve bir kanvas seçip aktar butonuna
                  basın, çalışmanız burada görüntülenecektir.
                </p>
                <p className="text-body-small-regular text-text-secondary">
                  En iyi sonuçlar için yatay, kare ve dikey oranda 3 master ekleyin.
                </p>
              </div>

              <div className="border-t border-border-primary" />

              <div className="flex flex-col items-center gap-small px-large py-large">
                <p className="text-body-small-regular text-text-secondary">
                  Henüz eklentiyi yüklemediniz mi? Eklentiyi İndirin
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => void download()}
                  disabled={downloading}
                >
                  {downloading ? 'Hazırlanıyor…' : 'Figma Plugin İndir'}
                </Button>
                {error && <p className="text-body-small-regular text-error-text">{error}</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
